import React, { useState } from "react";
import { Link } from "react-router-dom";
import { XCircle, ArrowRight } from "lucide-react";
import { useLanguage } from "../../i18n";

// Water Screen Gallery Images
const WATER_SCREEN_IMAGES = [
  {
    id: 'wsg-1',
    url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/lc9lzn46_6F12EF87-F46F-4FFE-8533-A9B40475E226.png',
    title: { es: 'Cotton Silk', en: 'Cotton Silk' },
    description: { es: 'Activación de marca corporativa', en: 'Corporate brand activation' }
  },
  {
    id: 'wsg-2',
    url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/0aeqa8md_751D6A44-4C45-41C2-9CC2-460CB22448CF.png',
    title: { es: 'NexAir Sky Marketing', en: 'NexAir Sky Marketing' },
    description: { es: 'Show con drones y water screen', en: 'Drone and water screen show' }
  },
  {
    id: 'wsg-3',
    url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/51aunvpj_77D8F1F5-F142-4E10-ACE9-DA7343FE1DE5.png',
    title: { es: 'Park Residences', en: 'Park Residences' },
    description: { es: 'Lanzamiento inmobiliario', en: 'Real estate launch' }
  },
  {
    id: 'wsg-4',
    url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/twojddqd_IMG_1247.avif',
    title: { es: 'Ave de Fuego', en: 'Fire Bird' },
    description: { es: 'Proyección artística sobre agua', en: 'Artistic projection on water' }
  },
];

const WaterScreenGallerySection = () => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const { language } = useLanguage();

  return (
    <section id="water-screen" className="py-24 md:py-32 bg-[#030810]" data-testid="water-screen-gallery-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {language === 'es' ? 'Pantalla de' : 'Water'}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              {language === 'es' ? 'Agua' : 'Screen'}
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {language === 'es' 
              ? 'Proyecciones espectaculares sobre cortinas de agua que transforman cualquier espacio en un escenario mágico.'
              : 'Spectacular projections on water curtains that transform any space into a magical stage.'}
          </p>
        </div>
        
        {/* Gallery Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {WATER_SCREEN_IMAGES.map((image) => (
            <div 
              key={image.id}
              className="group relative aspect-[3/4] rounded-xl overflow-hidden cursor-pointer border border-white/10 hover:border-cyan-500/30 transition-all"
              onClick={() => setLightboxImage(image)}
              data-testid={`water-screen-item-${image.id}`}
            >
              <img 
                src={image.url} 
                alt={image.title[language]}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform">
                <h4 className="text-white font-bold">{image.title[language]}</h4>
                <p className="text-gray-300 text-sm">{image.description[language]}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <a 
            href="#contacto"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold transition-all hover:shadow-[0_0_30px_rgba(0,240,255,0.4)]"
          >
            {language === 'es' ? 'Cotizar Pantalla de Agua' : 'Quote Water Screen'}
          </a>
          <Link 
            to="/pantalla-de-agua"
            className="inline-flex items-center gap-2 px-6 py-3 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            {language === 'es' ? 'Ver más' : 'Learn more'}
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <XCircle size={40} />
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img 
              src={lightboxImage.url} 
              alt={lightboxImage.title[language]}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center">
              <h3 className="text-white text-xl font-bold">{lightboxImage.title[language]}</h3>
              <p className="text-gray-400">{lightboxImage.description[language]}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default WaterScreenGallerySection;
